import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import type { IColumnsMap, IFiltersMap } from 'libs/design-system/src/types/data_table';
import { DataTable } from '@unified-checkout/design-system';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useLangStore } from '@app/dashboard/store/lang';
import { getQuickLinks } from '@app/dashboard/services/inner_layout/quick-links';
import { getCountryFromDomain } from '@app/dashboard/utils/getCountryFromDomain';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { mainPreDefinedRanges } from '@app/dashboard/utils/dateRangePicker';
import { formatToISODateTimeUTC } from '@app/dashboard/utils/dateFormats';
import { LINK_STATES } from '@app/dashboard/constants/quick-links';
import { Header, type IQuickLinksFilters } from './Header';
import styles from './quick-links.module.scss';

const TABLE_ID = 'quick_links_table';

export const QuickLinks = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();
  const [searchParams] = useSearchParams();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [isError, setIsError] = useState(false);
  const [dateRange, setDateRange] = useState<IQuickLinksFilters>(() => {
    const defaultPreDefinedRange = mainPreDefinedRanges(t)?.find((range) => range.default);
    if (defaultPreDefinedRange?.selectionRange[0] && defaultPreDefinedRange?.selectionRange[1]) {
      const [startDate, endDate] = defaultPreDefinedRange.selectionRange;
      return {
        created_at__gte: formatToISODateTimeUTC(startDate, '+03:00', 'start'),
        created_at__lte: formatToISODateTimeUTC(endDate, '+03:00', 'end'),
      };
    }
    return {};
  });

  const columnsMap: IColumnsMap = [
    {
      id: 'id',
      name: t('innerLayout.quickLinksPage.table.headers.paymentLinkId'),
      type: 'text',
      key: 'id',
    },
    {
      id: 'date',
      name: t('innerLayout.quickLinksPage.table.headers.createdAt'),
      type: 'date',
      key: 'created_at',
      hintText: 'created_at',
    },
    {
      id: 'amount',
      name: t('innerLayout.quickLinksPage.table.headers.amount'),
      type: 'amount_cents',
      key: 'amount_cents',
      additionalText: {
        key: 'currency',
        placement: lang === 'en' ? 'start' : 'end',
      },
    },
    {
      id: 'merchant_payment_link_id',
      name: t(
        'innerLayout.quickLinksPage.table.headers.merchantPaymentLinkId'
      ),
      type: 'text',
      key: 'reference_id',
    },
    {
      id: 'client_name',
      name: t('innerLayout.quickLinksPage.table.headers.clientName'),
      type: 'text',
      key: 'client_info.full_name',
    },
    {
      id: 'phone',
      name: t('innerLayout.quickLinksPage.table.headers.phone'),
      type: 'text',
      key: 'client_info.phone_number',
      format: (_: any, value: any) => {
        if (!value) return '-';
        let phoneNumber = String(value);

        // In Arabic (RTL), use Unicode directional markers to force LTR display
        // so the + appears on the left side visually
        if (lang === 'ar') {
          return `\u202A${phoneNumber}\u202C`;
        }
        return phoneNumber;
      },
    },
    {
      id: 'status',
      name: t('innerLayout.quickLinksPage.table.headers.status'),
      type: 'badge',
      key: 'state',
      labelComparingValue: 'state',
      label: {
        [LINK_STATES.PAID]: { color: 'green', text: t('innerLayout.quickLinksPage.filters.states.paid') },
        [LINK_STATES.UNPAID]: { color: 'yellow', text: t('innerLayout.quickLinksPage.filters.states.unpaid') },
        created: { color: 'yellow', text: t('innerLayout.quickLinksPage.filters.states.unpaid') },
        [LINK_STATES.CANCELLED]: { color: 'red', text: t('innerLayout.quickLinksPage.filters.states.cancelled') },
        [LINK_STATES.DEACTIVATED]: { color: 'yellow', text: t('innerLayout.quickLinksPage.filters.states.deactivated') },
      },
      labelFallback: { color: 'yellow', text: t('innerLayout.quickLinksPage.filters.states.unpaid') },
    },
    {
      id: 'expired',
      name: t('innerLayout.quickLinksPage.table.headers.expired'),
      type: 'text',
      key: 'expires_at',
      format: (_: any, value: any) => {
        if (value === null) return t('innerLayout.no');
        if (!value) return '-';
        const expiresAt = new Date(value);
        const now = new Date();
        const isExpired = expiresAt < now;
        return isExpired ? t('innerLayout.yes') : t('innerLayout.no');
      },
    },
  ];

  const filtersMap: IFiltersMap = useMemo(() => {
    const currencies: Record<string, string>[] = [
      { 'EGP': 'EGP' },
      { 'USD': 'USD' },
      { 'AED': 'AED' },
      { 'SAR': 'SAR' },
      { 'OMR': 'OMR' },
      { 'PKR': 'PKR' },
    ];

    const states: Record<string, string>[] = [
      { [t('innerLayout.quickLinksPage.filters.states.paid')]: 'paid' },
      { [t('innerLayout.quickLinksPage.filters.states.unpaid')]: 'created' },
      { [t('innerLayout.quickLinksPage.filters.states.cancelled')]: 'canceled' },
    ];

    return [
      {
        type: 'text',
        key: 'reference_id',
        name: t('innerLayout.quickLinksPage.filters.referenceId'),
        values: [],
        placeHolder: t('innerLayout.quickLinksPage.filters.referenceId')
      },
      {
        type: 'select',
        key: 'currency',
        name: t('innerLayout.quickLinksPage.filters.currency'),
        values: currencies,
        defaultSelection: 'all'
      },
      {
        type: 'select',
        key: 'state',
        name: t('innerLayout.quickLinksPage.filters.status'),
        values: states,
        defaultSelection: 'all'
      },
      // {
      //   type: 'text',
      //   key: 'amount_cents__gte',
      //   name: t('innerLayout.quickLinksPage.filters.amountMin'),
      //   values: [],
      //   placeHolder: t('innerLayout.quickLinksPage.filters.amountMin'),
      //   validations: [
      //     {
      //       regex: '^\\d+(\\.\\d+)?$',
      //       message: t('innerLayout.quickLinksPage.filters.errors.amountMustBeNumber')
      //     }
      //   ]
      // },
      // {
      //   type: 'text',
      //   key: 'amount_cents__lte',
      //   name: t('innerLayout.quickLinksPage.filters.amountMax'),
      //   values: [],
      //   placeHolder: t('innerLayout.quickLinksPage.filters.amountMax'),
      //   validations: [
      //     {
      //       regex: '^\\d+(\\.\\d+)?$',
      //       message: t('innerLayout.quickLinksPage.filters.errors.amountMustBeNumber')
      //     }
      //   ]
      // },
      {
        type: 'text',
        key: 'client_url',
        name: t('innerLayout.quickLinksPage.filters.paymentLinkUrl'),
        values: [],
        placeHolder: t('innerLayout.quickLinksPage.filters.paymentLinkUrl'),
        validations: [
          {
            regex: '^https?://.+',
            message: t('innerLayout.quickLinksPage.filters.errors.invalidUrl')
          }
        ]
      },
      {
        type: 'phone',
        key: 'phone_number',
        name: t('innerLayout.quickLinksPage.filters.phoneNumber'),
        values: [],
        placeHolder: t('innerLayout.quickLinksPage.filters.phoneNumber'),
        validations: []
      },
    ];
  }, [t, searchParams]);

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_QUICK_LINKS, isLive, dateRange],
    [isLive, dateRange]
  );

  const queryFunctionParams = useMemo(
    () => ({
      is_live: isLive,
      ...dateRange,
    }),
    [isLive, dateRange]
  );

  const resetFiltersParams = useMemo(() => [isLive], [isLive]);

  const handleTableRowClick = (rowObj: any) => {
    if (!rowObj) return;
    const path = INNER_ROUTES.SINGLE_QUICK_LINK.replace(':id', String(rowObj.id));
    navigate(path, { state: { is_multiple: rowObj?.is_multiple } });
  };

  return (
    <div className={styles['quick-links-wrapper']}>
      <Header
        filters={dateRange}
        onFiltersChange={setDateRange}
        appliedFiltersCount={0}
      />

      <DataTable
        lang={lang}
        filters={{
          filtersMap: filtersMap,
          isMobile: isMobile,
          country: getCountryFromDomain(),
        }}
        tableBody={{
          lang: lang,
          tableId: TABLE_ID,
          dataLocation: 'data.results',
          columnsMap: columnsMap,
          rowClickHandler: handleTableRowClick,
        }}
        pagination={{
          hideLabels: isMobile,
          type: 'cursor_based',
          dataLocation: {
            next: 'data.next',
            prev: 'data.previous',
          },
          isMobile: isMobile,
        }}
        queries={{
          queryKeys: queryKeys,
          queryFunction: getQuickLinks,
          queryFunctionParams: queryFunctionParams,
          resetFiltersParams: resetFiltersParams,
        }}
        errorState={{
          onError: (error) => setIsError(error),
        }}
        emptyState={{
          text: t('innerLayout.quickLinksPage.table.body.noQuickLinksYet'),
          description: t(
            'innerLayout.quickLinksPage.table.body.noQuickLinksHint'
          ),
        }}
      />
    </div>
  );
};
